import React, { useEffect } from "react";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { MealPatternNameKind } from "@/gql/graphql";
import useNumberInputState from "@/hooks/useNumberInputState";
import { colors } from "@/theme/overrides/colors";
import { getPortionWeightGrams, mealPatternNameLabel } from "@/utils/food";
import { round } from "@/utils/number";

import { PlannedFood, PlannedFoodPortion } from "./utils";
import MealPatternIcon from "../MealPlanForm/MealPlannerForm/MealPatternIcon";

interface Props {
  name: MealPatternNameKind;
  open: boolean;
  food: PlannedFood;
  foodportion: PlannedFoodPortion;
  onClose: () => void;
  onAddMealPattern: (
    name: MealPatternNameKind,
    amountServings: number,
    food: PlannedFood,
    foodPortion: PlannedFoodPortion
  ) => void;
  title?: string;
  defaultAmount?: number;
}

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: 450,
    paddingBottom: "32px",
  },
  title: {
    padding: theme.spacing(3, 4),
  },
  titleText: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.dark,
  },
  closeButton: {
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  content: {
    padding: 0,
  },
  nutrientColumn: {
    margin: theme.spacing(3, -4),
    width: `calc(100% + ${theme.spacing(8)})`,
  },
  foodName: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.dark,
  },
  nutrient: {
    position: "relative",
    padding: theme.spacing(4),
  },
  hdivider: {
    width: "100%",
    backgroundColor: "#EDF0F5",
  },
  vdivider: {
    backgroundColor: "#EDF0F5",
  },
  nutrientCircle: {
    position: "absolute",
    top: 20,
    borderRadius: "50%",
    border: "5px solid #F76C41",
    width: "108px",
    height: "108px",
  },
  nutrientName: {
    fontSize: 14,
    color: colors.gray,
  },
  nutrientValue: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.dark,
    zIndex: 10,
  },
  nutrientPercent: {
    fontSize: 16,
    fontWeight: 700,
    color: "#2D87BB",
  },
  measurement: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.gray,
  },
  portionField: {
    width: "100%",
  },
  amountField: {
    width: "100%",
  },
  amountButton: {
    width: "100%",
    height: "100%",
    border: "1px solid #EDF0F5",
    color: colors.gray,
    backgroundColor: colors.light,
  },
  decAmountButton: {
    borderRight: 0,
  },
  incAmountButton: {
    borderLeft: 0,
  },
}));

const AddMealPatternDialog: React.FC<Props> = ({
  name,
  open,
  food,
  foodportion,
  onClose,
  onAddMealPattern,
  title,
  defaultAmount,
}) => {
  const { classes, cx } = useStyles();
  const [amountNum, amountStr, setAmount] = useNumberInputState({
    default: defaultAmount ?? 1,
    min: 0,
    maxDecimalPlace: 2,
  });

  useEffect(() => {
    if (defaultAmount != null) setAmount(defaultAmount);
  }, [defaultAmount]);

  const renderDialogTitle = () => {
    return (
      <DialogTitle className={classes.title}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography className={classes.titleText}>
            {title ?? "Add Item Portions"}
          </Typography>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
    );
  };

  const weight = getPortionWeightGrams(
    {
      amount: foodportion?.amount ?? 0,
      weightGrams: foodportion?.weightGrams ?? 0,
    },
    amountNum
  );
  const weightPercent = weight / (food?.epWeightGrams ?? 0);

  const macroValue = (macro: number | null | undefined) => {
    if (food) {
      if (macro == null)
        return (
          <>
            <Typography className={classes.nutrientPercent}>&nbsp;</Typography>
            <Typography className={classes.nutrientValue}>N/A</Typography>
          </>
        );

      const percentage = Math.round((macro / food.epWeightGrams) * 100);
      return (
        <>
          <Typography className={classes.nutrientPercent}>
            {percentage}%
          </Typography>
          <Typography className={classes.nutrientValue}>
            {round(macro * weightPercent, 2)}g
          </Typography>
        </>
      );
    }
  };

  const addFood = () => {
    if (food && foodportion) {
      onAddMealPattern(name, amountNum, food, foodportion);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.paper,
      }}
    >
      {renderDialogTitle()}
      <DialogContent className={classes.content}>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" alignItems="center" p={2}>
            <MealPatternIcon name={name} />
            <Typography className={classes.foodName}>
              {mealPatternNameLabel(name)}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Divider className={classes.hdivider} />
            <Box display="flex" flexDirection="row">
              <Box
                display="flex"
                flexDirection="column"
                sx={{ position: "relative", minWidth: "30%", maxWidth: "30%" }}
                alignItems="center"
                justifyContent="center"
                padding={4}
              >
                <div className={classes.nutrientCircle}></div>
                <Typography className={classes.nutrientPercent}>
                  &nbsp;
                </Typography>
                <Typography className={classes.nutrientValue}>
                  {round((food?.energyKcal ?? 0) * amountNum)}
                </Typography>
                <Typography className={classes.nutrientName}>
                  Calories
                </Typography>
              </Box>
              <Divider
                className={classes.vdivider}
                orientation="vertical"
                flexItem
              />
              <Grid container>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={4}
                  >
                    {macroValue(food?.carbohydrateGrams)}
                    <Typography className={classes.nutrientName}>
                      Carbohydrate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={4}
                  >
                    {macroValue(food?.fatGrams)}
                    <Typography className={classes.nutrientName}>
                      Fat
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={4}
                  >
                    {macroValue(food?.proteinGrams)}
                    <Typography className={classes.nutrientName}>
                      Protein
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box mb={5} />
          <Box display="flex" flexDirection="column" margin={3}>
            <Typography className={classes.measurement}>Servings</Typography>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} container>
                <Grid item xs={8}>
                  <TextField
                    className={classes.amountField}
                    value={amountStr}
                    onChange={(e) => setAmount(e.target.value)}
                    type="number"
                  />
                </Grid>
                <Grid item xs={2}>
                  <ButtonBase
                    className={cx(
                      classes.amountButton,
                      classes.decAmountButton
                    )}
                    onClick={() => setAmount(amountNum - 1)}
                  >
                    <RemoveIcon />
                  </ButtonBase>
                </Grid>
                <Grid item xs={2}>
                  <ButtonBase
                    className={cx(
                      classes.amountButton,
                      classes.incAmountButton
                    )}
                    onClick={() => setAmount(amountNum + 1)}
                  >
                    <AddIcon />
                  </ButtonBase>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box mb={5} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              sx={{ textTransform: "initial" }}
              size="large"
              onClick={addFood}
              disabled={amountNum <= 0}
            >
              Add to diet
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddMealPatternDialog;
